import { useEffect, useRef, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { Button, CircularProgress, Grid, Paper, Stack } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  exportTransactions,
  getAllActiveProviders,
  getAllTransactions,
  getTransactionRelatedData,
  getTransactionsByProvider,
} from "../../../Api/Signin/Transaction.Api";
import { ReactComponent as Export } from "../../../Assets/Images/Icon/ExportICON.svg";
import { ReactComponent as InfoIcon } from "../../../Assets/Images/InfoIconGrey.svg";
import Calender from "../../../Components/atoms/Calender/CalenderDateRange/Calender";
import SearchInput from "../../../Components/atoms/Input/SearchInput";
import { SelectInput } from "../../../Components/atoms/Input/SelectInput";
import { NoRecordsFound } from "../../../Components/atoms/NoRecordsFound/NoRecordsFound";
import { NoMaxWidthTooltip } from "../../../Components/atoms/Tooltip/NoMaxWidthTooltip";
import {
  Heading1,
  Heading3,
  Heading5,
} from "../../../Components/atoms/Typography/Heading/Heading";
import { BasicGrid } from "../../../Components/organisms/BasicGrid/BasicGrid";
import {
  downloadReportExport,
  getReportExportErrorMessage,
} from "../../../Utils/FileDownload/FileDownload";
import {
  convertUTCToLocal2,
  formatDate,
  formatTime1,
} from "../../../Utils/TimeFormat/TimeFormat";
import { getProviderDisplayName } from "../../../Utils/WordFormatter/ProviderAlias";
import { schema } from "./shema";

const formatCurrency = (val) => {
  if (val === null || val === undefined || isNaN(val)) return "0.00";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val);
};

const formatStripeStatus = (value) => {
  const status = String(value ?? "")
    .trim()
    .toLowerCase();

  if (!status || status === "-") return "-";
  if (status.includes("revers")) return "Reversed";
  if (status.includes("refund")) return "Refunded";
  if (status === "2" || status === "cancelled" || status === "canceled") {
    return "Cancelled";
  }
  if (status === "1" || status.includes("captur")) return "Captured";
  if (
    status === "0" ||
    status === "pre auth" ||
    status === "pre-auth" ||
    status === "preauth"
  ) {
    return "Pre-auth";
  }
  if (status === "succeeded" || status === "success" || status === "paid") {
    return "Succeeded";
  }

  return String(value).trim();
};

const appointmentStatusNames = {
  1: "Reserved",
  2: "Confirmed",
  3: "Scheduled",
  4: "Cancelled",
  5: "Completed",
  6: "Rescheduled",
  7: "Ongoing",
  8: "Unscheduled",
  9: "Missed",
  10: "Expired",
  11: "Unsuccessful",
  12: "Successful",
};

const formatAppointmentStatus = (value) => {
  if (value === null || value === undefined || value === "") return "-";

  const statusId = Number(value);
  if (Number.isInteger(statusId) && appointmentStatusNames[statusId]) {
    return appointmentStatusNames[statusId];
  }

  const status = String(value).trim().toLowerCase();
  const match = Object.values(appointmentStatusNames).find(
    (name) => name.toLowerCase() === status,
  );
  return match || String(value).trim();
};

const buildFirstDoctorColumns = (t, i18n) => [
  {
    fieldtext: t("common:table-field.tx-id"),
    field: "TXId",
    width: "160px",
    textAlign: "Left",
  },
  {
    fieldtext: t("glossary:finance-transactions.txdatetime"),
    field: "TXDate",
    width: "176px",
    textAlign: "Left",
    template: (p) =>
      `${formatDate(convertUTCToLocal2(p.TXDate), i18n.language)} ${formatTime1(
        convertUTCToLocal2(p.TXDate),
        i18n.language,
      )}`,
  },
  {
    fieldtext: "Stripe status",
    field: "StripeStatus",
    width: "160px",
    textAlign: "Left",
    template: (p) =>
      formatStripeStatus(p?.StripeStatus ?? p?.PaymentStatus),
  },
  {
    fieldtext: "Appointment Status",
    field: "AppointmentStatus",
    width: "180px",
    textAlign: "Left",
    template: (p) => formatAppointmentStatus(p?.AppointmentStatus),
  },
  {
    fieldtext: "Specialty",
    field: "Specialty",
    width: "180px",
    textAlign: "Left",
    template: (p) => (p?.Specialty != null ? p.Specialty : "-"),
  },
  {
    fieldtext: t("common:table-field.patient-id"),
    field: "PatientCode",
    width: "130px",
    textAlign: "Left",
  },
  {
    fieldtext: t("common:table-field.doctor-id"),
    field: "DocId",
    width: "130px",
    textAlign: "Left",
  },
  {
    fieldtext: "Doctor name",
    field: "DoctorName",
    width: "200px",
    textAlign: "Left",
    template: (p) => p?.DoctorName || "-",
  },
  {
    fieldtext: "Repeat code",
    field: "RepeatCode",
    width: "150px",
    textAlign: "Left",
    template: (p) => p?.RepeatCode || "-",
  },
  {
    fieldtext: t("common:table-field.discount"),
    field: "Discount",
    width: "170px",
    textAlign: "left",
    enableGroupByFormat: true,
    template: (p) => (p?.Discount != null ? `Rs.${p.Discount}` : "-"),
  },
  {
    fieldtext: t("glossary:finance-transactions.gross-consultation-fee"),
    field: "GrossConsultationFee",
    width: "220px",
    textAlign: "left",
    enableGroupByFormat: true,
    template: (p) =>
      p?.GrossConsultationFee != null ? `Rs.${p.GrossConsultationFee}` : "-",
  },
  {
    fieldtext: t("glossary:finance-transactions.net-consultation-fee"),
    field: "NetConsultationFee",
    width: "230px",
    textAlign: "left",
    enableGroupByFormat: true,
    template: (p) =>
      p?.NetConsultationFee != null ? `Rs.${p.NetConsultationFee}` : "-",
  },
  {
    fieldtext: t("common:table-field.convenience-fee"),
    field: "ConvenienceFee",
    width: "230px",
    textAlign: "Left",
    enableGroupByFormat: true,
    template: (p) =>
      p?.ConvenienceFee != null ? `Rs.${p.ConvenienceFee}` : "-",
  },
  // SSCL and VAT injected here dynamically
  {
    fieldtext: t("common:table-field.total-collected"),
    field: "TotalCollected",
    width: "200px",
    textAlign: "left",
    enableGroupByFormat: true,
    template: (p) =>
      p?.TotalCollected != null ? `Rs.${p.TotalCollected}` : "-",
  },
  {
    fieldtext: t("glossary:finance-transactions.pgateway"),
    field: "PaymentGatewayFee",
    width: "180px",
    textAlign: "left",
    enableGroupByFormat: true,
    template: (p) =>
      p?.PaymentGatewayFee != null ? `Rs.${p.PaymentGatewayFee}` : "-",
  },
  {
    fieldtext: t("glossary:finance-transactions.net-convenience-fee"),
    field: "NetConvenienceFee",
    width: "230px",
    textAlign: "left",
    enableGroupByFormat: true,
    template: (p) =>
      p?.NetConvenienceFee != null ? `Rs.${p.NetConvenienceFee}` : "-",
  },
  {
    fieldtext: "Mode of consultation",
    field: "Mode of consultation",
    width: "220px",
    textAlign: "left",
    enableGroupByFormat: true,
    template: (p) => {
      if (p?.ModeId === 1) {
        if (p?.IsDoctorCreated === true) return "Online (Doctor) - Paid clinic";
        if (p?.IsAssistantCreated === true)
          return "Online (Assistant) - Paid clinic";
        return "Online (Patient)";
      }
      if (p?.ModeId === 2) {
        if (p?.IsDoctorCreated === true)
          return "Walk-in (Doctor) - Paid clinic";
        if (p?.IsAssistantCreated === true)
          return "Walk-in (Assistant) - Paid clinic";
        return "Walk-in (Patient)";
      }
      return "-";
    },
  },
  {
    fieldtext: "Payment mode",
    field: "Payment mode",
    width: "220px",
    textAlign: "left",
    enableGroupByFormat: true,
    template: (p) => (p?.paymentMethod != null ? `${p.paymentMethod}` : "-"),
  },
  {
    fieldtext: "Location name",
    field: "Location name",
    width: "220px",
    textAlign: "left",
    enableGroupByFormat: true,
    template: (p) => (p?.ClinicName != null ? `${p.ClinicName}` : "-"),
  },
  {
    fieldtext: "Address",
    field: "Address",
    width: "220px",
    textAlign: "left",
    enableGroupByFormat: true,
    template: (p) => (p?.Address != null ? `${p.Address}` : "-"),
  },
];

export const Transactions = () => {
  // ------------------------------------------------ Start => Deifinitions/ inits --------------------------------------------------

  const { t, i18n } = useTranslation();

  const INITIAL_FIRST_DOCTOR_COLUMNS = buildFirstDoctorColumns(t, i18n);

  const paperRef = useRef(null);
  const gridRef = useRef(null);
  const originalColumnsRef = useRef(null);
  const [grid, setGrid] = useState();

  const [transactionDetails, setTransactionDetails] = useState();
  const [totalRecordCount, setTotalRecordCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchText, setSearchText] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [paperWidth, setPaperWidth] = useState();
  const [didFirstTimeRunTs, setDidFirstTimeRunTs] = useState(false);
  const [pageData, setPageData] = useState({
    currentPage: 1,
    pageCount: 0,
    pageSize: 10,
    totalPages: Math.ceil(totalRecordCount / pageSize),
    totalRecordsCount: 0,
  });

  const [columnData, setColumnData] = useState(() =>
    buildFirstDoctorColumns(t, i18n),
  );

  const PROVIDER_FIRSTDOCTOR = "first doctor";
  const { control, trigger, watch, setValue, getValues } = useForm({
    defaultValues: {
      dateRange: 1,
      datePickerRange: [],
      providersList: PROVIDER_FIRSTDOCTOR,
    },
    resolver: yupResolver(schema(t)),
    mode: "onChange",
  });

  const [providerList, setProviderList] = useState([
    { label: "Firstdoctor", key: PROVIDER_FIRSTDOCTOR, id: null }
  ]);
  const [selectedProvider, setSelectedProvider] =
    useState(PROVIDER_FIRSTDOCTOR);
  const watchAll = watch();
  const isCustomDateRange = Number(watchAll.dateRange) === 0;
  const filterControlGap = isCustomDateRange ? 1.25 : 2;
  const filterInputSx = {
    "& > div": {
      width: "100%",
    },
    "& .MuiFormControl-root, & .MuiInputBase-root": {
      width: "100%",
    },
    "& .MuiInputBase-input": {
      boxSizing: "border-box",
      width: "100%",
    },
  };
  const filterSearchSx = {
    flex: isCustomDateRange ? "1 1 377px" : "0 0 377px",
    minWidth: isCustomDateRange ? "170px" : "377px",
    maxWidth: "377px",
    ...filterInputSx,
  };
  const filterProviderSx = {
    flex: isCustomDateRange ? "0 1 176px" : "0 0 176px",
    minWidth: isCustomDateRange ? "132px" : "176px",
    maxWidth: "176px",
    ...filterInputSx,
  };
  const filterDateRangeSx = {
    flex: isCustomDateRange ? "0 1 176px" : "0 0 176px",
    minWidth: isCustomDateRange ? "124px" : "176px",
    maxWidth: "176px",
    ...filterInputSx,
  };

  const clearErrorState = () => {
    setIsError(false);
    setErrorMessage("");
  };

  const showExportError = (message) => {
    setIsError(true);
    setErrorMessage(message);
  };

  const getExportErrorMessage = (message) =>
    getReportExportErrorMessage(message, {
      noRecordsMessage: t("glossary:finance-transactions.no-records-found"),
      serverErrorMessage: t("validation:server-error"),
    });

  const getSelectedProviderId = (provider = selectedProvider) => {
    if (provider === PROVIDER_FIRSTDOCTOR) {
      return null;
    }

    return (
      providerList.find((item) => item.key === provider.toLowerCase())?.id ||
      null
    );
  };

  const getNormalizedSearchText = () => {
    const trimmedValue =
      typeof searchText === "string" ? searchText.trim() : "";

    return trimmedValue.length > 0 ? trimmedValue : null;
  };

  const updateColumnsForProvider = async (provider) => {
    if (provider !== PROVIDER_FIRSTDOCTOR) {
      const otherProviderCols = [
        { fieldtext: "TXID", field: "TXId", width: "160px", textAlign: "Left" },
        {
          fieldtext: "TX date/time",
          field: "TXDate",
          width: "176px",
          textAlign: "Left",
          template: (p) =>
            `${formatDate(
              convertUTCToLocal2(p.TXDate),
              i18n.language,
            )} ${formatTime1(convertUTCToLocal2(p.TXDate), i18n.language)}`,
        },
        {
          fieldtext: "Stripe status",
          field: "StripeStatus",
          width: "160px",
          textAlign: "Left",
          template: (p) =>
            formatStripeStatus(p?.StripeStatus ?? p?.PaymentStatus),
        },
        {
          fieldtext: "Specialty",
          field: "Specialty",
          width: "180px",
          textAlign: "Left",
          template: (p) => p?.Specialty || "-",
        },
        {
          fieldtext: "Patient ID",
          field: "PatientCode",
          width: "130px",
          textAlign: "Left",
        },
        {
          fieldtext: "Doctor ID",
          field: "DocId",
          width: "130px",
          textAlign: "Left",
        },
        {
          fieldtext: "Doctor name",
          field: "DoctorName",
          width: "200px",
          textAlign: "Left",
          template: (p) => p?.DoctorName || "-",
        },
        {
          fieldtext: "Repeat code",
          field: "RepeatCode",
          width: "150px",
          textAlign: "Left",
          template: (p) => p?.RepeatCode || "-",
        },
        {
          fieldtext: "Discount (Rs.)",
          field: "Discount",
          width: "170px",
          textAlign: "left",
          template: (p) => (p?.Discount != null ? `Rs.${p.Discount}` : "-"),
        },
        {
          fieldtext: "Doctor fee",
          field: "DoctorFee",
          width: "220px",
          textAlign: "left",
          template: (p) => `Rs.${formatCurrency(p.DoctorFee)}`,
        },
        {
          fieldtext: "Hospital fee",
          field: "HospitalFee",
          width: "170px",
          textAlign: "left",
          template: (p) => `Rs.${formatCurrency(p.HospitalFee)}`,
        },
        {
          fieldtext: "VAT",
          field: "VAT",
          width: "230px",
          textAlign: "left",
          template: (p) => `Rs.${formatCurrency(p.VAT)}`,
        },
        {
          fieldtext: "Convenience fee",
          field: "ConvenienceFee",
          width: "230px",
          textAlign: "Left",
          template: (p) => `Rs.${formatCurrency(p.ConvenienceFee)}`,
        },
        {
          fieldtext: "Total collected",
          field: "TotalCollected",
          width: "200px",
          textAlign: "left",
          template: (p) => `Rs.${formatCurrency(p.TotalCollected)}`,
        },
        {
          fieldtext: "PGateway fee",
          field: "PaymentGatewayFee",
          width: "180px",
          textAlign: "left",
          template: (p) => `Rs.${formatCurrency(p.PaymentGatewayFee)}`,
        },
        {
          fieldtext: "Mode of consultation",
          field: "ModeId",
          width: "220px",
          textAlign: "left",
          template: (p) => (p.ModeId === 1 ? "Online" : "Walk-in"),
        },
        {
          fieldtext: "Payment mode",
          field: "paymentMethod",
          width: "220px",
          textAlign: "left",
        },
        {
          fieldtext: "Location name",
          field: "ClinicName",
          width: "220px",
          textAlign: "left",
        },
        {
          fieldtext: "Appointment Status",
          field: "AppointmentStatus",
          width: "200px",
          textAlign: "left",
          template: (p) => formatAppointmentStatus(p?.AppointmentStatus),
        },
      ];
      setColumnData(otherProviderCols);
    } else {
      await GetTransactionRelated(PROVIDER_FIRSTDOCTOR);
    }
  };

  // Get transactions
  const GetTransactions = async (pgNo, pgSize, provider) => {
    const currentProvider = provider ?? selectedProvider;
    const isFirstDoctor = currentProvider === PROVIDER_FIRSTDOCTOR;

    try {
      clearErrorState();
      setIsDataLoading(true);
      const formValues = watch();
      const result = getDateRange(formValues.dateRange);

      let response;

      if (isFirstDoctor) {
        response = await getAllTransactions(
          pgNo || pageNumber,
          pgSize || pageSize,
          searchText,
          result.startDate,
          result.endDate,
        );
      } else {
        const providerItem = providerList.find(
          (p) => p.key === currentProvider.toLowerCase(),
        );

        response = await getTransactionsByProvider(
          pgNo || pageNumber,
          pgSize || pageSize,
          providerItem?.id,
          searchText,
          result.startDate,
          result.endDate,
        );
      }

      if (response?.type === 0 || response?.code === 200) {
        let items;

        if (isFirstDoctor) {
          items = response.data?.transactions
            ? JSON.parse(response.data.transactions)
            : null;
          setTotalRecordCount(response.data?.totalCount ?? 0);
        } else {
          items = response.data?.[0] ? JSON.parse(response.data[0]) : null;
          setTotalRecordCount(response.totalRecordsCount ?? 0);
        }

        setTransactionDetails(items);
        await updateColumnsForProvider(currentProvider);
      } else {
        setTransactionDetails(null);
        setTotalRecordCount(0);
      }
    } catch (error) {
      console.error("GetTransactions Error:", error);
      setTransactionDetails(null);
    } finally {
      setIsDataLoading(false);
    }
  };

  // ------------------------------------------------ End => Deifinitions/ inits ------------------------------------------------------

  // ---------------------------------------------- Start => Action functions ---------------------------------------------------------
  useEffect(() => {
    getProviderList();
    (async () => {
      setPaperWidth(paperRef.current.offsetWidth);
      await GetTransactionRelated();
      await GetTransactions();
      setDidFirstTimeRunTs(true);
    })();
  }, []);

  useEffect(() => {
    if (providerList.length > 0) {
      requestAnimationFrame(() => {
        setValue("providersList", PROVIDER_FIRSTDOCTOR);
      });
    }
  }, [providerList]);

  useEffect(() => {
    const newProvider = watch().providersList;
    if (newProvider && newProvider !== selectedProvider) {
      setSelectedProvider(newProvider);
    }
  }, [watch().providersList]);

  // Handle when changing the rows per page (page size)
  useEffect(() => {
    if (didFirstTimeRunTs) {
      setPageData((prevPageData) => ({
        ...prevPageData,
        pageSize: pageSize,
        totalPages: Math.ceil(totalRecordCount / pageSize),
      }));
      setPageNumber(1);
      pageNumber == 1 && GetTransactions();
    }
  }, [pageSize]);

  // Handle when page number is changed
  useEffect(() => {
    if (didFirstTimeRunTs) {
      GetTransactions();
      setPageData({ ...pageData, currentPage: pageNumber });
    }
  }, [pageNumber]);

  // Handle when total record count is changed
  useEffect(() => {
    setPageData({
      currentPage: 1,
      pageCount: 0,
      pageSize: 10,
      totalPages: Math.ceil(totalRecordCount / pageSize),
      totalRecordsCount: 0,
    });
    setPageNumber(1);
  }, [totalRecordCount]);

  // Handle transaction list on filter change
  useEffect(() => {
    if (didFirstTimeRunTs) {
      setPageNumber(1);
      GetTransactions();
    }
  }, [searchText]);

  // Handle transaction list on date range change
  useEffect(() => {
    if (didFirstTimeRunTs) {
      if (watch().dateRange == 0) {
        setShowDatePicker(true);
      } else {
        setShowDatePicker(false);
        setValue("datePickerRange", []);
        setPageNumber(1);
        setPageSize(10);

        setPageData({
          currentPage: 1,
          pageCount: 0,
          pageSize: 10,
          totalPages: Math.ceil(totalRecordCount / 10),
          totalRecordsCount: 0,
        });
        GetTransactions(1, 10);
      }
    }
  }, [watch().dateRange]);

  useEffect(() => {
    if (didFirstTimeRunTs) {
      setTransactionDetails(null);
      setTotalRecordCount(0);
      setPageNumber(1);
      setPageSize(10);
      setPageData({
        currentPage: 1,
        pageCount: 0,
        pageSize: 10,
        totalPages: 0,
        totalRecordsCount: 0,
      });
      GetTransactions(1, 10, selectedProvider);
    }
  }, [selectedProvider]);

  // Handle transaction list on date picker range change
  useEffect(() => {
    if (didFirstTimeRunTs) {
      (async () => {
        if (watch().datePickerRange.length > 0) {
          await trigger();
          if (watch().datePickerRange.length == 2) {
            setPageNumber(1);
            setPageSize(10);
            setPageData({
              currentPage: 1,
              pageCount: 0,
              pageSize: 10,
              totalPages: Math.ceil(totalRecordCount / 10),
              totalRecordsCount: 0,
            });
            GetTransactions(1, 10);
          }
        }
      })();
    }
  }, [watch().datePickerRange]);

  // Handle page size click
  const handlePageSizeChange = (selectedPageSize) => {
    setPageSize(selectedPageSize);
  };

  // Handle page number click
  const handlePaginationClick = (pageNumber) => {
    setPageNumber(pageNumber);
  };

  const setupColumnsIncludingDynamic = async (taxList) => {
    try {
      // Create tax field objects
      let taxFieldObjects = taxList.map((availableTax) => ({
        fieldtext: availableTax.name + " (Rs.)",
        field: availableTax.id,
        textAlign: "Left",
        enableGroupByFormat: true,
        taxRate: availableTax.percentage,
        template: (props) => {
          if (availableTax.name === "SSCL") {
            const taxAmount =
              props.SSCL != null && availableTax.percentage != null
                ? props.SSCL
                : "0";
            return `Rs. ${taxAmount}`;
          } else if (availableTax.name === "VAT") {
            const taxAmount =
              props.VAT != null && availableTax.percentage != null
                ? props.VAT
                : "0";
            return `Rs. ${taxAmount}`;
          }
        },
      }));

      let baseColumns = [...INITIAL_FIRST_DOCTOR_COLUMNS];
      const convenienceFeeIndex = baseColumns.findIndex(
        (col) => col.field === "ConvenienceFee",
      );

      if (convenienceFeeIndex === -1) {
        console.error("Convenience Fee column not found in columnData");
        return;
      }

      // Check for VAT and SSCL existence
      const existsVat = taxFieldObjects.some(
        (obj) => obj.fieldtext === "VAT (Rs.)",
      );
      const existsSscl = taxFieldObjects.some(
        (obj) => obj.fieldtext === "SSCL (Rs.)",
      );

      let newColumnData = [...baseColumns.slice(0, convenienceFeeIndex + 1)];

      if (existsSscl) {
        newColumnData.push(
          ...taxFieldObjects.filter((obj) => obj.fieldtext === "SSCL (Rs.)"),
        );
      }
      if (existsVat) {
        newColumnData.push(
          ...taxFieldObjects.filter((obj) => obj.fieldtext === "VAT (Rs.)"),
        );
      }

      newColumnData.push(...baseColumns.slice(convenienceFeeIndex + 1));

      newColumnData.push(
        ...taxFieldObjects.filter(
          (obj) =>
            obj.fieldtext !== "VAT (Rs.)" && obj.fieldtext !== "SSCL (Rs.)",
        ),
      );

      setColumnData(newColumnData);
    } catch (error) {
      console.error("Error in setupColumnsIncludingDynamic:", error);
    }
  };

  // Get transaction related data
  const GetTransactionRelated = async (providerOverride) => {
    const provider = providerOverride ?? selectedProvider;
    if (provider !== PROVIDER_FIRSTDOCTOR) return;
    try {
      let response = await getTransactionRelatedData();
      if (response.type === 0) {
        let taxList = response.data;
        await setupColumnsIncludingDynamic(taxList);
      }
    } catch (exception) {
      console.error("GetTransactionRelated error:", exception);
    }
  };

  const getProviderList = async () => {
    try {
      const response = await getAllActiveProviders();
      const providers = response.data;
      const optionsList = providers
        .map((p) => ({
          label: getProviderDisplayName(p.providerName),
          key: p.providerName.toLowerCase(),
          id: p.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setProviderList(optionsList);
    } catch (error) {
      console.error("Failed to fetch providers", error);
    }
  };

  // Get date range based on user dropdown selection
  function getDateRange(monthsCount, customDateRange) {
    let startDate = null;
    let endDate = null;

    if (monthsCount == 0) {
      const datePickerRange = customDateRange || watch().datePickerRange;
      startDate = getFormattedDate(datePickerRange?.[0], "yyyy-mm-dd");
      endDate = getFormattedDate(datePickerRange?.[1], "yyyy-mm-dd");
    } else {
      const currentDate = new Date();
      startDate = new Date(currentDate);
      startDate.setMonth(currentDate.getMonth() - monthsCount);
      startDate = getFormattedDate(startDate, "yyyy-mm-dd");
      endDate = getFormattedDate(currentDate, "yyyy-mm-dd");
    }

    return {
      startDate,
      endDate,
    };
  }

  // Returns formatted date in "yyyy-mm-dd", "ddmmyyyy", "dd/mm/yyyy"
  const getFormattedDate = (date, customFormat) => {
    const convertedDate = new Date(date);
    const year = convertedDate.getFullYear();
    const month = String(convertedDate.getMonth() + 1).padStart(2, "0");
    const day = String(convertedDate.getDate()).padStart(2, "0");
    let formattedDate = "";

    switch (customFormat) {
      case "yyyy-mm-dd":
        formattedDate = `${year}-${month}-${day}`;
        break;
      case "ddmmyyyy":
        formattedDate = `${day}${month}${year}`;
        break;
      case "dd/mm/yyyy":
        formattedDate = `${day}/${month}/${year}`;
        break;
      default:
        formattedDate = `${year}-${month}-${day}`;
    }
    return formattedDate;
  };

  // Export transactions to excel - Backend export
  const handleTransactionsExport = async () => {
    const currentValues = getValues();
    const isCustomRange = Number(currentValues.dateRange) === 0;
    const isDateRangeValid = isCustomRange
      ? await trigger("datePickerRange")
      : true;
    const formValues = getValues();
    const result = getDateRange(
      formValues.dateRange,
      formValues.datePickerRange,
    );
    const providerId = getSelectedProviderId();

    if (selectedProvider !== PROVIDER_FIRSTDOCTOR && !providerId) {
      showExportError(t("validation:server-error"));
      return;
    }

    if (
      result.startDate !== "NaN-NaN-NaN" &&
      result.endDate !== "NaN-NaN-NaN" &&
      (!isCustomRange || isDateRangeValid)
    ) {
      try {
        clearErrorState();
        setIsExportLoading(true);

        const response = await exportTransactions({
          reportName: "FinanceTransactions",
          fileType: "xlsx",
          startDate: result.startDate,
          endDate: result.endDate,
          searchString: getNormalizedSearchText(),
          providerId,
        });

        if (response?.type === 0 && response?.data) {
          downloadReportExport(response, {
            reportName: "Transactions",
            fileType: "xlsx",
          });
          return;
        }

        showExportError(getExportErrorMessage(response?.message));
      } catch (exception) {
        showExportError(getExportErrorMessage(exception?.message));
      } finally {
        setIsExportLoading(false);
      }
    }
  };

  //Custom Headers
  const customHeaderTemplate = (props) => {
    if (props.headerText === t("common:table-field.total-collected")) {
      return (
        <Stack
          justifyContent={"flex-start"}
          alignItems={"center"}
          height={"100%"}
          direction={"row"}
          spacing={"4px"}
          width={"100%"}
        >
          <Heading5 text={props.headerText} />
          <NoMaxWidthTooltip
            title={
              "Net consultation fee (Rs.) + Convenience fee (Rs.) + SSCL (Rs.) + VAT (Rs.)"
            }
            placement="top"
            arrow
          >
            <InfoIcon />
          </NoMaxWidthTooltip>
        </Stack>
      );
    } else if (props.headerText === t("common:table-field.convenience-fee")) {
      return (
        <Stack
          justifyContent={"flex-start"}
          alignItems={"center"}
          height={"100%"}
          direction={"row"}
          spacing={"4px"}
          width={"100%"}
        >
          <Heading5 text={props.headerText} />
          <NoMaxWidthTooltip
            title={"Gross consultation fee (Rs.) * Convenience fee %"}
            placement="top"
            arrow
          >
            <InfoIcon />
          </NoMaxWidthTooltip>
        </Stack>
      );
    } else if (
      props.headerText ===
      t("glossary:finance-transactions.net-consultation-fee")
    ) {
      return (
        <Stack
          justifyContent={"flex-start"}
          alignItems={"center"}
          height={"100%"}
          direction={"row"}
          spacing={"4px"}
          width={"100%"}
        >
          <Heading5 text={props.headerText} />
          <NoMaxWidthTooltip
            title={"Gross consultation fee (Rs.) - Discount (Rs.)"}
            placement="top"
            arrow
          >
            <InfoIcon />
          </NoMaxWidthTooltip>
        </Stack>
      );
    } else if (
      props.headerText ===
      t("glossary:finance-transactions.net-convenience-fee")
    ) {
      return (
        <Stack
          justifyContent={"flex-start"}
          alignItems={"center"}
          height={"100%"}
          direction={"row"}
          spacing={"4px"}
          width={"100%"}
        >
          <Heading5 text={props.headerText} />
          <NoMaxWidthTooltip
            title={"Convenience fee (Rs.) - PGateway fee (Rs.)"}
            placement="top"
            arrow
          >
            <InfoIcon />
          </NoMaxWidthTooltip>
        </Stack>
      );
    } else if (props.headerText === "VAT (Rs.)") {
      return (
        <Stack
          justifyContent={"flex-start"}
          alignItems={"center"}
          height={"100%"}
          direction={"row"}
          spacing={"4px"}
          width={"100%"}
        >
          <Heading5 text={props.headerText} />
          <NoMaxWidthTooltip
            title={"Convenience fee (Rs.) * VAT %"}
            placement="top"
            arrow
          >
            <InfoIcon />
          </NoMaxWidthTooltip>
        </Stack>
      );
    } else if (props.headerText === "SSCL (Rs.)") {
      return (
        <Stack
          justifyContent={"flex-start"}
          alignItems={"center"}
          height={"100%"}
          direction={"row"}
          spacing={"4px"}
          width={"100%"}
        >
          <Heading5 text={props.headerText} />
          <NoMaxWidthTooltip
            title={"Convenience fee (Rs.) * SSCL %"}
            placement="top"
            arrow
          >
            <InfoIcon />
          </NoMaxWidthTooltip>
        </Stack>
      );
    } else if (
      props.headerText === t("common:table-field.tx-id") ||
      props.headerText === t("glossary:finance-transactions.txdatetime") ||
      props.headerText === t("common:table-field.patient-id") ||
      props.headerText === t("common:table-field.doctor-id") ||
      props.headerText === t("glossary:finance-transactions.gross-consultation-fee") ||
      props.headerText === t("common:table-field.discount") ||
      props.headerText === t("glossary:finance-transactions.pgateway") ||
      props.headerText === "Mode of consultation" ||
      props.headerText === "Payment mode" ||
      props.headerText === "Location name" ||
      props.headerText === "Address" ||
      props.headerText === "Specialty" ||
      props.headerText === "Stripe status" ||
      props.headerText === "Appointment Status" ||
      props.headerText === "Appointment status"
    ) {
      return (
        <Stack
          justifyContent={"center"}
          alignItems={props.headerTextAlign || "flex-start"}
          height={"100%"}
        >
          <Heading5 text={props.headerText} />
        </Stack>
      );
    } else {
      return (
        <Stack
          justifyContent={"flex-start"}
          alignItems={"center"}
          height={"100%"}
          direction={"row"}
          spacing={"4px"}
          width={"100%"}
        >
          <Heading5 text={props.headerText} />
          <NoMaxWidthTooltip
            title={t("glossary:finance-transactions.tax-calculate")}
            placement="top"
            arrow
          >
            <InfoIcon />
          </NoMaxWidthTooltip>
        </Stack>
      );
    }
  };

  // ------------------------------------------------- End => Action functions --------------------------------------------------------

  return (
    <>
      <Grid container direction={"column"}>
        <Grid item>
          <Stack direction={"column"} justifyContent={"space-between"} mb={2.8}>
            <Stack
              direction={"row"}
              spacing={2}
              width={"100%"}
              justifyContent={"space-between"}
              alignItems={"center"}
              sx={{
                flexWrap: "nowrap",
                minWidth: 0,
              }}
            >
              <Heading1 text={t("glossary:finance-transactions.title")} />
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"flex-end"}
                marginRight={"20px"}
                sx={{
                  flex: "1 1 auto",
                  minWidth: 0,
                  columnGap: filterControlGap,
                  flexWrap: "nowrap",
                }}
              >
                <Stack sx={filterSearchSx}>
                  <SearchInput
                    searchValue={searchText}
                    placeholder={t(
                      "glossary:finance-transactions.placeholder.tx-id-doc-name",
                    )}
                    width={"100%"}
                    color={"#FFF"}
                    getSearchText={(val) => {
                      setSearchText(val);
                      setPageData({ ...pageData, currentPage: 1 });
                    }}
                  />
                </Stack>

                <Stack sx={filterProviderSx}>
                  <SelectInput
                    size={"small"}
                    width={"100%"}
                    height={"48px"}
                    control={control}
                    backgroundColor={"#FFF"}
                    options={providerList}
                    name={"providersList"}
                    menuItemLabel={"label"}
                    menuItemKey={"key"}
                  />
                </Stack>

                <Stack sx={filterDateRangeSx}>
                  <SelectInput
                    size={"small"}
                    width={"100%"}
                    height={"48px"}
                    control={control}
                    backgroundColor={"#FFF"}
                    options={[
                      {
                        label: t("glossary:finance-transactions.dates.month1"),
                        key: 1,
                      },
                      {
                        label: t("glossary:finance-transactions.dates.month3"),
                        key: 3,
                      },
                      {
                        label: t("glossary:finance-transactions.dates.month6"),
                        key: 6,
                      },
                      {
                        label: t("glossary:finance-transactions.dates.year1"),
                        key: 12,
                      },
                      {
                        label: t("glossary:finance-transactions.dates.custom"),
                        key: 0,
                      },
                    ]}
                    name={"dateRange"}
                    menuItemLabel={"label"}
                    menuItemKey={"key"}
                  />
                </Stack>

                {showDatePicker && (
                  <Stack
                    sx={{
                      flex: "0 0 auto",
                      marginRight: { xs: "17px", xl: "30px" },
                      marginTop: "3px",
                    }}
                  >
                    <Calender
                      control={control}
                      name="datePickerRange"
                      required={true}
                      width={"270px"}
                      enableMaxDate={true}
                    />
                  </Stack>
                )}

                <Stack sx={{ flex: "0 0 auto" }}>
                  <Button
                    disableTouchRipple
                    disabled={isExportLoading}
                    variant="lightBlue"
                    sx={{
                      borderRadius: "14px",
                      paddingLeft: "20px",
                      paddingRight: "20px",
                      paddingTop: "14px",
                      paddingBottom: "14px",
                      gap: "10px",
                      height: "auto",
                      marginTop: "3px",
                    }}
                    onClick={handleTransactionsExport}
                  >
                    {isExportLoading ? (
                      <CircularProgress
                        size={20}
                        sx={{ color: "#ffffff", marginRight: "10px" }}
                      />
                    ) : (
                      <Export style={{ marginRight: "10px" }} fill="#ffffff" />
                    )}
                    <Heading3
                      text={t("glossary:finance-transactions.export")}
                      color="#ffffff"
                    />
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Grid>
        <Grid item width="100%">
          <>
            {isError && (
              <div style={{ marginBottom: "10px" }}>
                <NoRecordsFound label={errorMessage} isError={true} />
              </div>
            )}
            <Paper
              ref={paperRef}
              sx={{
                padding: "20px 24px",
                boxShadow: "0px 0px 25px 0px rgba(0, 0, 0, 0.05)",
                position: "relative",
                maxWidth: paperWidth,
                overflowX: "auto",
              }}
            >
              <div style={{ position: "relative" }}>
                <div
                  style={{
                    display: "contents",
                  }}
                >
                  <BasicGrid
                    data={transactionDetails}
                    noRecFoundMessage={t(
                      "glossary:finance-transactions.no-records-found"
                    )}
                    isLoading={isDataLoading}
                    columnArray={columnData}
                    allowSorting
                    allowPaging
                    rowSelect={false}
                    customSearchText={watchAll.searchText}
                    field={[]}
                    pageData={pageData}
                    paginationClick={handlePaginationClick}
                    pageSizeChange={handlePageSizeChange}
                    customHeaderCell={customHeaderTemplate}
                  />
                </div>
              </div>
            </Paper>
          </>
        </Grid>
      </Grid>
    </>
  );
};
